// ── Types ─────────────────────────────────────────────────────────────────
export type {
  PickerHighlight,
  ClipboardData,
  DynamicNodeConfig,
} from "./types";

// ── Right panel layout ─────────────────────────────────────────────────────
export { rightPanelWidthAtom } from "./focusedEditor";

// ── Atoms (state) ─────────────────────────────────────────────────────────
export {
  nodesAtom,
  edgesAtom,
  activeFlowIdAtom,
  workflowTypeAtom,
  pendingLayoutFlowIdAtom,
  canvasMeasuredAtom,
  existingNodeTypesAtom,
  saveStateAtom,
  hoveredNodeAtom,
  edgeHighlightNodeAtom,
  edgeDragSourceAtom,
  edgeDropTargetNodeIdAtom,
  dragOverContainerIdAtom,
  selectedLoopIterationsAtom,
  openPickerAtPositionAtom,
  pickerHighlightAtom,
  hoveredPartialRunNodeAtom,
  renamingNodeIdAtom,
  saveWorkflowHashAtom,
  saveBaseRevisionAtom,
  saveConflictAtom,
  selectedNodeIdAtom,
  progressFocusedNodeAtom,
  seeNodeInFlowAtom,
  runNodeStatusesAtom,
  isValidDataAtom,
  flowValidationV2Atom,
  previewRunIdAtom,
  previewSnapshotAtom,
  viewingVersionIdAtom,
  isPreviewRunModeAtom,
  isViewingVersionAtom,
  isEditorReadOnlyAtom,
  isCanvasNodesDraggableAtom,
  nodeRegistryAtom,
  runTriggersAtom,
  openPickerNearNodeAtom,
  contextMenuNodeIdAtom,
  chipHighlightNodeIdAtom,
  requestedSectionAtom,
  simpleModeAtom,
  dynamicNodeConfigsAtom,
  nodeDynamicDisplayAtom,
  nodesFetchingConfigAtom,
  areConfigEndpointsLoadedAtom,
  nodeConfigLoadingAtom,
  flowLoadGenerationAtom,
  nodeConfigErrorsAtom,
  pendingNodeConfigRefreshAtom,
  requestNodeConfigRefreshAtom,
  nodeContentStreamFamilyAtom,
  streamingNodeIdsAtom,
  secretsPanelOpenAtom,
  canvasUseSmoothEdgesAtom,
  canvasZoomModeAtom,
} from "./atoms";
export type {
  NodeStreamKey,
  FlowValidationNode,
  FlowValidationResult,
} from "./atoms";

// ── Actions: Nodes ────────────────────────────────────────────────────────
export {
  addNodeAtom,
  addStickyNoteV2Atom,
  duplicateStickyNoteV2Atom,
  updateNodeConfigAtom,
  updateVariableBindingsAtom,
  setIterateOverrideAtom,
  setIterateModesAtom,
  renameNodeAtom,
  togglePinnedFieldAtom,
  toggleDescriptionCardPinAtom,
  setPinnedFieldsAtom,
  useAddNewNode,
} from "./actions/nodes";

// ── Actions: Edges ────────────────────────────────────────────────────────
export {
  applyNodeChangesAtom,
  applyEdgeChangesAtom,
  connectAtom,
  connectStartAtom,
  connectOnDropAtom,
  updateEdgeDropTargetAtom,
  isValidBodyDropTarget,
  isGroupEdgeDropTarget,
  unattachableEndpointReason,
  nodeDragStartAtom,
  nodeDragStopAtom,
} from "./actions/edges";

// ── Actions: Canvas ───────────────────────────────────────────────────────
export {
  groupSelectedNodesV2Atom,
  ungroupNodesV2Atom,
  removeNodesFromGroupV2Atom,
  convertSelectionToSubflowV2Atom,
  updateDragOverContainerAtom,
  reparentNodesOnDropAtom,
  autoArrangeAtom,
} from "./actions/canvas";

// ── Actions: Clipboard ────────────────────────────────────────────────────
export {
  copyNodesAtom,
  cutNodesAtom,
  pasteNodesAtom,
  hasClipboardData,
} from "./actions/clipboard";

// ── Actions: Persistence ──────────────────────────────────────────────────
export {
  markDirtyAtom,
  resetEditorAtom,
  saveWorkflowAtom,
  saveAsNewVersionAtom,
  loadWorkflowAtom,
  loadWorkflowPreviewAtom,
  useAutoSave,
  useSave,
} from "./actions/persistence";

// ── Actions: Validation ───────────────────────────────────────────────────
export { triggerValidationAtom } from "./actions/validation";

// ── Actions: History ──────────────────────────────────────────────────────
export {
  undoAtom,
  redoAtom,
  undoToIndexAtom,
  redoToIndexAtom,
  closeCollapseAtom,
} from "./actions/history";
export {
  historyStackAtom,
  isApplyingHistoryAtom,
  pendingFormFlushAtom,
  canUndoAtom,
  canRedoAtom,
  MAX_HISTORY,
} from "./history/atoms";
export type { Transaction, HistoryStack } from "./history/types";

// ── Selectors (per-node) ──────────────────────────────────────────────────
export {
  nodeDataMapAtom,
  nodeDataByIdAtom,
  nodeCountAtom,
  selectedNodeMissingAtom,
  validationFingerprintAtom,
  hoveredPartialWorkflowStructureAtom,
  nodeShapeMapAtom,
  graphNodesAtom,
  isInputHighlightedAtom,
  isGhostedByHoverAtom,
  isGhostedByPartialRunAtom,
  isChipHighlightedAtom,
  hasActiveChipHighlightAtom,
  highlightedSourceNodeIdAtom,
  nodeRunProgressAtom,
  nodeErrorsAtom,
  isRenamingAtom,
  hasContextMenuAtom,
  isPickerSourceAtom,
  isPickerTargetAtom,
  isPickerActiveAtom,
  isEdgeDropTargetAtom,
  nodeConnectedHandlesAtom,
  nodeEffectiveTypesAtom,
  nodeIoFetchPathAtom,
  nodeSourceEdgesAtom,
} from "./selectors/node-selectors";

// ── Helpers (public) ──────────────────────────────────────────────────────
export {
  getOutputHandles,
  getInputHandles,
  getRegistryConfigDefaults,
  getRegistryPinnedFields,
  serializeDefinitionV2,
  definitionNodeToReactFlow,
  createStickyNoteReactFlowNode,
  createGroupReactFlowNode,
  getStickyNoteViewportPosition,
  STICKY_NOTE_VIEWPORT_JITTER_PX,
  STICKY_NOTE_DEFINITION_TYPE,
  GROUP_DEFINITION_TYPE,
  isStickyNoteDefinitionType,
  isGroupDefinitionType,
  isStickyNoteReactFlowNode,
  isCanvasOnlyReactFlowNode,
} from "./helpers";

// ── Groups (geometry) ─────────────────────────────────────────────────────
export {
  absoluteNodePosition,
  nodeSize,
  isGroupReactFlowNode,
  parentGroupOf,
  groupContainingPoint,
  expandGroupFrameToContain,
  growGroupsToFitChildren,
} from "./groups";
